import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getMention,
  updateMention,
  updateMentionCommandCenter,
} from '../api/client.js';
import MentionActivityLog from '../components/MentionActivityLog.jsx';
import SentimentLayout from '../components/SentimentLayout.jsx';
import SentimentBadge from '../components/SentimentBadge.jsx';
import SeverityBadge from '../components/SeverityBadge.jsx';
import SourceIcon from '../components/SourceIcon.jsx';
import TagChip from '../components/TagChip.jsx';
import UrgentBadge from '../components/UrgentBadge.jsx';
import {
  formatMentionDate,
  MENTION_SEVERITIES,
  MENTION_STATUSES,
  PRODUCT_LABELS,
  ratingStars,
  sourceMeta,
} from '../utils/sentiment.js';

export default function MentionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [mention, setMention] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [status, setStatus] = useState('unreviewed');
  const [severity, setSeverity] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activityKey, setActivityKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMention(id)
      .then((m) => {
        if (cancelled) return;
        setMention(m);
        setStatus(m.status || 'unreviewed');
        setSeverity(m.severity || '');
        setNotes(m.notes || '');
      })
      .catch((e) => !cancelled && setLoadError(e.message || 'Failed to load'));
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const updated = await updateMention(id, {
        status,
        severity: severity || null,
        notes,
      });
      setMention(updated);
      setSuccess('Saved.');
      setActivityKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleCommandCenter(submitted) {
    try {
      setError(null);
      const updated = await updateMentionCommandCenter(id, submitted);
      setMention(updated);
      setActivityKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Update failed');
    }
  }

  async function toggleUrgent(next) {
    try {
      setError(null);
      const updated = await updateMention(id, { is_urgent: next });
      setMention(updated);
      setActivityKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Update failed');
    }
  }

  if (loadError) {
    return (
      <SentimentLayout title="Mention">
        <button className="btn btn--secondary" onClick={() => navigate('/sentiment')}>
          ← Back
        </button>
        <div className="form-error" style={{ marginTop: 12 }}>{loadError}</div>
      </SentimentLayout>
    );
  }

  if (!mention) {
    return (
      <SentimentLayout title="Mention">
        <div className="empty-state">Loading…</div>
      </SentimentLayout>
    );
  }

  const meta = sourceMeta(mention.source_type);

  return (
    <SentimentLayout title="Mention Detail">
      <button className="btn btn--secondary" onClick={() => navigate('/sentiment')}>
        ← Back to Sentiment
      </button>

      <div className="detail-grid" style={{ marginTop: 12 }}>
        <div>
          <section className="card detail-section">
            <div className="card__header">
              <h2 style={{ fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SourceIcon type={mention.source_type} size={18} /> {meta.label}
              </h2>
              <div className="badges-row">
                <SeverityBadge value={mention.severity} />
                <SentimentBadge value={mention.sentiment} score={mention.sentiment_score} />
                {mention.is_urgent && <UrgentBadge />}
              </div>
            </div>
            <div className="card__body">
              <div className="detail-block">
                <div className="detail-block__label">Published</div>
                <div className="detail-block__value">{formatMentionDate(mention.published_at)}</div>
              </div>
              {mention.location_name && (
                <div className="detail-block">
                  <div className="detail-block__label">Location</div>
                  <div className="detail-block__value">{mention.location_name}</div>
                </div>
              )}
              {mention.rating != null && (
                <div className="detail-block">
                  <div className="detail-block__label">Rating</div>
                  <div className="detail-block__value">
                    {ratingStars(mention.rating)} ({mention.rating}/5)
                  </div>
                </div>
              )}
              <div className="detail-block">
                <div className="detail-block__label">Author</div>
                <div className="detail-block__value">{mention.author_name || 'Unknown'}</div>
              </div>
              <div className="detail-block">
                <div className="detail-block__label">Content</div>
                <div className="detail-text">{mention.content || '— (no text)'}</div>
              </div>
              {mention.url && (
                <div className="detail-block">
                  <a href={mention.url} target="_blank" rel="noopener noreferrer">
                    View original post ↗
                  </a>
                </div>
              )}
              {mention.tags && mention.tags.length > 0 && (
                <div className="detail-block">
                  <div className="detail-block__label">Tags</div>
                  <div className="badges-row">
                    {mention.tags.map((t) => (
                      <TagChip key={t} label={t} />
                    ))}
                  </div>
                </div>
              )}
              {mention.products && mention.products.length > 0 && (
                <div className="detail-block">
                  <div className="detail-block__label">Products</div>
                  <div className="badges-row">
                    {mention.products.map((p) => (
                      <TagChip key={p} label={PRODUCT_LABELS[p] || p} />
                    ))}
                  </div>
                </div>
              )}
              {mention.is_migration_related && (
                <div className="migration-box">
                  <strong>⚠️ Potentially Evolution-related</strong>
                  <div style={{ marginTop: 6 }}>{mention.migration_reasoning || ''}</div>
                  {mention.migration_confidence && (
                    <div className="migration-box__confidence">
                      Confidence: {mention.migration_confidence}
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="admin-panel">
          <div className="card">
            <div className="card__header">
              <h2 className="admin-panel__heading">Admin Panel</h2>
            </div>
            <div className="card__body">
              <div className="field">
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={saving}>
                  {MENTION_STATUSES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Severity</label>
                <select value={severity || ''} onChange={(e) => setSeverity(e.target.value)} disabled={saving}>
                  <option value="">(keep current)</option>
                  {MENTION_SEVERITIES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Command Center</label>
                {mention.submitted_to_command_center ? (
                  <div className="cc-panel">
                    <div className="cc-panel__status">⚑ Submitted to Command Center</div>
                    {mention.command_center_submitted_at && (
                      <div className="cc-panel__meta">
                        Submitted {formatMentionDate(mention.command_center_submitted_at)}
                        {mention.command_center_submitted_by
                          ? ` by ${mention.command_center_submitted_by}`
                          : ''}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => toggleCommandCenter(false)}
                    >
                      Remove Flag
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn--primary btn--block"
                    onClick={() => toggleCommandCenter(true)}
                  >
                    Submit to Command Center
                  </button>
                )}
              </div>
              <div className="field">
                <label>Urgent</label>
                {mention.is_urgent ? (
                  <div className="cc-panel">
                    <div className="cc-panel__status">⚡ Flagged Urgent</div>
                    <button
                      type="button"
                      className="btn btn--secondary"
                      onClick={() => toggleUrgent(false)}
                    >
                      Clear Urgent
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn--secondary btn--block"
                    onClick={() => toggleUrgent(true)}
                  >
                    Mark Urgent
                  </button>
                )}
              </div>
              <div className="field">
                <label>Notes</label>
                <textarea
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={saving}
                  placeholder="Internal notes for the Marketing team…"
                />
              </div>
              {error && <div className="form-error">{error}</div>}
              {success && <div className="form-success">{success}</div>}
              <button
                className="btn btn--primary btn--block"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div className="card__header">
              <h2 className="admin-panel__heading">Activity Log</h2>
            </div>
            <div className="card__body">
              <MentionActivityLog mentionId={id} refreshKey={activityKey} />
            </div>
          </div>
        </aside>
      </div>
    </SentimentLayout>
  );
}
